/**
 * useAuth Hook
 * Provides authentication context and methods
 */

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthState } from '../types/User.types';
import { authService, ACCESS_DENIED_ERROR } from '../services/authService';

interface AuthContextType extends AuthState {
  login: () => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    error: null,
  });

  const navigate = useNavigate();

  // Initialize MSAL and check auth state on every page load.
  // authService.initialize() is guarded against double-invocation internally.
  useEffect(() => {
    initializeAuth();
  }, []);

  const initializeAuth = async () => {
    try {
      // initialize() calls msalInstance.initialize() + handleRedirectPromise().
      // On the redirect back from Microsoft, handleRedirectPromise() exchanges
      // the auth code for tokens and stores the account in session storage.
      await authService.initialize();

      if (authService.isAuthenticated()) {
        const user = await authService.getUserWithRole();
        setAuthState({
          user,
          isAuthenticated: true,
          isLoading: false,
          error: null,
        });
      } else {
        setAuthState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        });
      }
    } catch (error) {
      console.error('Auth initialization failed:', error);
      if (error instanceof Error && error.message === ACCESS_DENIED_ERROR) {
        setAuthState({ user: null, isAuthenticated: false, isLoading: false, error: 'Access denied' });
        navigate('/unauthorized', { replace: true });
        return;
      }
      setAuthState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: 'Failed to initialize authentication',
      });
    }
  };

  const login = async () => {
    try {
      setAuthState((prev) => ({ ...prev, isLoading: true, error: null }));
      await authService.login();
      // For real Azure AD: loginRedirect() navigates the browser away to Microsoft
      // so the line below never runs — initializeAuth() handles auth state on return.
      // For mock mode: login() stores mockAccount in localStorage and returns
      // immediately, so we must re-initialize to pick up the new auth state.
      await initializeAuth();
    } catch (error) {
      console.error('Login failed:', error);
      setAuthState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: 'Login failed. Please try again.',
      });
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
      setAuthState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
      setAuthState((prev) => ({ ...prev, error: 'Logout failed' }));
    }
  };

  const refreshUser = async () => {
    try {
      const user = await authService.getUserWithRole();
      setAuthState((prev) => ({ ...prev, user }));
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  };

  const value: AuthContextType = {
    ...authState,
    login,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
